import { AuthState } from './../models/authentication.model';
import { Injectable } from '@angular/core';
import { CanActivate, ActivatedRouteSnapshot, RouterStateSnapshot, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { Store } from '@ngrx/store';

import * as fromSelectors from './../store/selectors/authentication.selectors';
import * as fromActions from './../store/actions/authentication.actions';
import { tap, filter, take, switchMap, map, catchError } from 'rxjs/operators';
import { selectSportsState } from '../store/selectors/sports.selectors';
import { Action_Sports_Load } from '../store/actions/sports.actions';
import { Action_Sport_Init } from '../store/actions/sport-active.actions';
import { ApiService } from '../services/api.service';
import { ErrorService } from '../services/error.service';
import { DT } from '../helpers/date-time.helper';
import { UtilService } from '../services/util.service';

@Injectable()
export class CheckVersionGuard implements CanActivate {
  constructor(
    private store: Store<AuthState>
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return of(true);
  }

  checkStore(): Observable<boolean> {

    return this.store.select(fromSelectors.selectAuthLoaded).pipe(
      tap(loaded => {
        if (!loaded) {
          this.store.dispatch(new fromActions.Action_Auth_LoadCurrentFirebaseUser());
        }
      }),
      filter(loaded => loaded),
      take(1)
    );
  }

}

@Injectable()
export class IsAuthLoaded implements CanActivate {
  constructor(
    private store: Store<AuthState>
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return this.checkStore().pipe(
      switchMap(() => {
        return of(true);
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  checkStore(): Observable<boolean> {

    return this.store.select(fromSelectors.selectAuthLoaded).pipe(
      tap(loaded => {
        if (!loaded) {
          this.store.dispatch(new fromActions.Action_Auth_LoadCurrentFirebaseUser());
        }
      }),
      filter(loaded => loaded),
      take(1)
    );
  }

}

@Injectable()
export class IsSportsLoaded implements CanActivate {
  constructor(
    private store: Store<AuthState>,
    private util: UtilService
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return this.checkStore();
  }

  checkStore(): Observable<boolean> {

    return this.store.select(selectSportsState).pipe(
      tap((state => {
        if (state.error) {
          if (DT.DiffInSeconds(state.error_date) > 25) {
            this.store.dispatch(new Action_Sports_Load());
            return;
          } else {
            this.util.error.go({
              buttonText: 'Voltar',
              code: '1x2048e120',
              message: 'Não foi possível carregar a lista de esportes de nossos servidores. <br/>Tente novamente em alguns instantes.',
              autoexecute: 15
            }, false);
          }

        }
        if (!state.loaded && !state.loading && !state.error) {
          this.store.dispatch(new Action_Sports_Load());
        }
      })),
      filter(state => state.loaded),
      take(1),
      map(() => {
        this.store.dispatch(new Action_Sport_Init());
        return true;
      })
    );
  }

}

@Injectable()
export class IsProfileCompleted implements CanActivate {
  constructor(
    private store: Store<AuthState>,
    private router: Router
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return this.checkStore().pipe(
      map((completed) => {
        if (!completed) {
          this.router.navigate(['startup']);
          return true;
        }
        return true;
      }),
      catchError(() => of(false))
    );
  }

  checkStore(): Observable<boolean> {

    return this.store.select(fromSelectors.selectAuthState).pipe(
      filter(state => state.loaded),
      take(1),
      switchMap(() => this.store.select(fromSelectors.selectAuthUserPreferences).pipe(
        map(preferences => {
          return preferences.profile_completed;
        })
      ))
    );
  }

}

@Injectable()
export class IsAuthGuard implements CanActivate {
  constructor(
    private router: Router,
    private store: Store<AuthState>
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {


    return this.store.select(fromSelectors.selectAuthLoaded)
      .pipe(
        filter(loaded => loaded == true),
        take(1),
        switchMap(() => this.store.select(fromSelectors.selectAuthIsLogged).pipe(
          tap(logged => {
            if (!logged) {
              this.router.navigate(['signin']);
            }
          })
        ))
      );
  }

}


@Injectable()
export class CanSignInRoute implements CanActivate {
  constructor(
    private router: Router,
    private store: Store<AuthState>
  ) { }

  canActivate(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean> | Promise<boolean> | boolean {

    return this.store.select(fromSelectors.selectAuthLoaded)
      .pipe(
        filter(loaded => loaded == true),
        take(1),
        switchMap(() => this.store.select(fromSelectors.selectAuthState).pipe(
          map(logged => {
            if (logged.isLogged) {
              this.router.navigate(['home']);
              return false;
            }
            return true;
          })
        ))
      );
  }

}

